const initTabs = (root) => {
  root.querySelectorAll('[data-mdp-tabs]').forEach((tabs) => {
    const buttons = [...tabs.querySelectorAll('[data-mdp-tab]')];
    const panels = [...tabs.querySelectorAll('[data-mdp-panel]')];

    const activate = (target) => {
      buttons.forEach((button) => {
        const active = button.dataset.mdpTab === target;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const active = panel.dataset.mdpPanel === target;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => activate(button.dataset.mdpTab));
    });

    activate(buttons.find((button) => button.classList.contains('is-active'))?.dataset.mdpTab || buttons[0]?.dataset.mdpTab);
  });
};

// The gallery + zoom use ONE delegated listener on document instead of binding
// per-element. This survives the theme's view-transition / SPA navigation and
// any DOM morphing, so taps keep working no matter how the page was reached.
const closeZoomDialog = (dialog) => {
  if (!dialog) return;
  if (dialog.open && typeof dialog.close === 'function') {
    dialog.close();
  } else {
    dialog.removeAttribute('open');
  }
};

const openZoomDialog = (gallery) => {
  const main = gallery.querySelector('[data-mdp-media-main]');
  const dialog = gallery.querySelector('[data-mdp-zoom-dialog]');
  const zoomImage = gallery.querySelector('[data-mdp-zoom-image]');
  if (!main || !dialog || !zoomImage) return;

  zoomImage.src = main.dataset.mdpMediaFullSrc || main.currentSrc || main.src;
  zoomImage.alt = main.alt || '';

  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
};

let galleryDelegationBound = false;
const initGallery = () => {
  if (galleryDelegationBound) return;
  galleryDelegationBound = true;

  document.addEventListener('click', (event) => {
    // Thumbnail tapped → swap the main image.
    const thumb = event.target.closest('[data-mdp-media-src]');
    if (thumb) {
      const gallery = thumb.closest('[data-mdp-gallery]');
      const main = gallery && gallery.querySelector('[data-mdp-media-main]');
      if (main) {
        main.src = thumb.dataset.mdpMediaSrc;
        main.dataset.mdpMediaFullSrc = thumb.dataset.mdpMediaFullSrc || thumb.dataset.mdpMediaSrc;
        main.alt = thumb.dataset.mdpMediaAlt || '';
        gallery.querySelectorAll('[data-mdp-media-src]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }
      return;
    }

    // "Close" button or backdrop tapped → close the zoom dialog.
    if (event.target.closest('[data-mdp-zoom-close]') || event.target.matches('[data-mdp-zoom-dialog]')) {
      closeZoomDialog(event.target.closest('[data-mdp-zoom-dialog]'));
      return;
    }

    // Main image or "Tap to zoom" button → open the zoom dialog.
    const opener = event.target.closest('[data-mdp-zoom-open], [data-mdp-media-main]');
    if (opener) {
      const gallery = opener.closest('[data-mdp-gallery]');
      if (gallery) openZoomDialog(gallery);
    }
  });
};

// The zoom open/close is handled by the delegated listener in initGallery; the
// zoom-in cursor is set in CSS. Kept as a no-op so the init pipeline is unchanged.
const initZoomDialogs = () => {};

const initQuantity = (root) => {
  root.querySelectorAll('[data-mdp-qty]').forEach((control) => {
    const input = control.querySelector('input[name="quantity"]');
    if (!input) return;

    control.querySelectorAll('[data-mdp-qty-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const delta = Number(button.dataset.mdpQtyChange || 0);
        const min = Number(input.getAttribute('min') || 1);
        input.value = String(Math.max(min, Number(input.value || min) + delta));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
};

const initPlanPrices = (root) => {
  root.querySelectorAll('[data-mdp-plan-group]').forEach((group) => {
    const target = group.querySelector('[data-mdp-plan-price-target]');
    const radios = [...group.querySelectorAll('input[type="radio"][data-mdp-plan-price]')];
    if (radios.length === 0) return;

    const box = group.querySelector('[data-mdp-box]');
    const qty = group.querySelector('[data-mdp-qty]');

    const sync = () => {
      const checked = radios.find((radio) => radio.checked) || radios[0];
      const isBox = checked && checked.hasAttribute('data-mdp-box-plan');
      if (target) {
        if (isBox && box) {
          const totalEl = box.querySelector('[data-mdp-box-total]');
          target.textContent = (totalEl && totalEl.textContent) || checked.dataset.mdpPlanPrice || target.textContent;
        } else {
          target.textContent = checked.dataset.mdpPlanPrice || target.textContent;
        }
      }
      if (box) box.hidden = !isBox;
      if (qty) qty.hidden = isBox;
    };

    radios.forEach((radio) => radio.addEventListener('change', sync));
    sync();
  });
};

const initSticky = (root) => {
  root.querySelectorAll('[data-mdp-sticky]').forEach((bar) => {
    if (bar.dataset.mdpStickyBound) return;
    bar.dataset.mdpStickyBound = '1';

    const update = () => {
      const past = window.scrollY > 640;
      let blocked = false;
      if (bar.classList.contains('mdp-sticky-atc')) {
        const embed = document.querySelector('.mdp-box--embed:not([hidden])');
        const btn = document.querySelector('[data-mdp-box-add]:not(.visually-hidden)');
        const hit = embed || btn;
        if (hit) {
          const hitRect = hit.getBoundingClientRect();
          const stickyHeight = bar.offsetHeight || 140;
          const stickyTop = window.innerHeight - stickyHeight - 12;
          blocked = hitRect.bottom > stickyTop && hitRect.top < window.innerHeight;
        }
      }
      bar.classList.toggle('is-visible', past && !blocked);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  });
};

const initLazyVideos = (root) => {
  const videos = [...root.querySelectorAll('[data-mdp-lazy-video]')].filter((video) => !video.dataset.mdpVideoLoaded);
  if (videos.length === 0) return;

  const loadVideos = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    videos.forEach((video) => {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      video.dataset.mdpVideoLoaded = 'true';
      video.load();
      video.play?.().catch(() => {});
    });
  };

  const scheduleLoad = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadVideos, { timeout: 2500 });
    } else {
      window.setTimeout(loadVideos, 800);
    }
  };

  if (document.readyState === 'complete') {
    scheduleLoad();
  } else {
    window.addEventListener('load', scheduleLoad, { once: true });
  }
};

const toHeadingId = (text, index) => {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);

  return `mdp-heading-${index + 1}-${slug || 'section'}`;
};

const initArticleToc = (root) => {
  root.querySelectorAll('[data-mdp-toc]').forEach((toc) => {
    const article = toc.closest('.mdp-article');
    const body = article?.querySelector('[data-mdp-article-body]');
    const list = toc.querySelector('[data-mdp-toc-list]');
    if (!body || !list) return;

    const headings = [...body.querySelectorAll('h2, h3')].filter((heading) => {
      return heading.textContent.trim() && !heading.closest('.mdp-article-related, .mdp-article-faq, .mdp-article-sources');
    });

    list.replaceChildren();

    if (headings.length < 2) {
      toc.hidden = true;
      return;
    }

    headings.forEach((heading, index) => {
      if (!heading.id) heading.id = toHeadingId(heading.textContent.trim(), index);

      const item = document.createElement('li');
      if (heading.tagName === 'H3') item.classList.add('is-sub');

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      item.append(link);
      list.append(item);
    });
  });
};

// Subscriptions: the theme's stock add-to-cart handler drops the selling_plan
// (adds one-time instead of the subscription). When a selling plan is selected,
// intercept and do a clean /cart/add.js, then fire the cart:update event so the
// drawer opens/refreshes. One-time purchases keep using the theme's normal flow.
const initSubscriptionAdd = (root) => {
  root.querySelectorAll('product-form-component[data-mdp-plan-group]').forEach((pfc) => {
    const form = pfc.querySelector('form');
    if (!form || form.dataset.mdpSubBound) return;
    form.dataset.mdpSubBound = '1';
    const formId = form.getAttribute('id');

    const onClick = async (event) => {
      const checked = form.querySelector('input[name="selling_plan"]:checked');
      const plan = checked && checked.value;
      if (checked && checked.hasAttribute('data-mdp-box-plan')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const boxAdd = pfc.querySelector('[data-mdp-box-add]');
        if (boxAdd) boxAdd.click();
        return;
      }
      if (!plan) return; // one-time → let the theme's normal add-to-cart handle it

      // subscription → bypass the stock handler that loses the plan
      event.preventDefault();
      event.stopImmediatePropagation();

      const button = event.currentTarget;
      if (button.hasAttribute('disabled')) return;
      const idEl = form.querySelector('[name="id"]');
      const qtyEl = form.querySelector('[name="quantity"]');
      const id = idEl && idEl.value;
      const qty = (qtyEl && qtyEl.value) || '1';
      if (!id) return;

      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems && cartItems.dataset.sectionId;
      const body = new URLSearchParams({ id, quantity: qty, selling_plan: plan });
      if (sectionId) body.set('sections', sectionId);

      button.classList.add('is-loading');
      try {
        const url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
          body: body.toString(),
        });
        const json = await res.json();
        if (json.status) { console.error('[mdp] subscription add failed', json); return; }
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: json,
            sourceId: formId,
            data: { source: 'mdp-subscription-add', itemCount: Number(qty) || 1, productId: pfc.dataset.productId, sections: json.sections },
          },
        }));
      } catch (err) {
        console.error('[mdp] subscription add error', err);
      } finally {
        button.classList.remove('is-loading');
      }
    };

    pfc.querySelectorAll('button.mdp-atc[type="submit"]').forEach((b) => b.addEventListener('click', onClick, true));
    document.querySelectorAll('.mdp-sticky-atc button.mdp-atc[form="' + formId + '"]').forEach((b) => b.addEventListener('click', onClick, true));
  });
};

const formatBoxMoney = (cents, format) => {
  if (window.Shopify && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents, format);
  }

  const value = Number(cents) || 0;
  const abs = Math.abs(value);
  const grouped = (separator, thousand) => {
    const [whole, fraction] = (abs / 100).toFixed(2).split('.');
    const withThousand = thousand ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousand) : whole;
    return `${withThousand}${separator}${fraction}`;
  };
  const tokens = {
    amount_with_comma_separator: grouped(',', '.'),
    amount_no_decimals_with_comma_separator: String(Math.round(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    amount_with_apostrophe_separator: grouped('.', "'"),
    amount_with_space_separator: grouped(',', ' '),
    amount_with_period_and_space_separator: grouped('.', ' '),
    amount_no_decimals: String(Math.round(abs / 100)),
    amount: grouped('.', ','),
  };

  let output = format || '€{{amount_with_comma_separator}}';
  Object.keys(tokens)
    .sort((a, b) => b.length - a.length)
    .forEach((key) => {
      output = output.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), tokens[key]);
    });

  if (value < 0 && output.indexOf('-') === -1) output = `-${output}`;
  return output;
};

const selectedBoxItems = (box) => {
  const slots = [...box.querySelectorAll('[data-mdp-box-slot]')];
  return slots.map((slot) => {
    const option = slot.selectedOptions[0];
    return {
      id: option && option.dataset.variantId,
      cents: option ? Number(option.dataset.cents) : 0,
      compareCents: option ? Number(option.dataset.compareCents) : 0,
      disabled: option ? option.disabled : true,
    };
  });
};

const initHouseholdBox = (root) => {
  root.querySelectorAll('[data-mdp-box]').forEach((box) => {
    if (box.dataset.mdpBoxBound) return;
    box.dataset.mdpBoxBound = '1';

    const totalEl = box.querySelector('[data-mdp-box-total]');
    const compareEl = box.querySelector('[data-mdp-box-compare]');
    const errorEl = box.querySelector('[data-mdp-box-error]');
    const addBtn = box.querySelector('[data-mdp-box-add]');
    const addLabel = box.querySelector('[data-mdp-box-add-label]');
    const moneyFormat = box.dataset.moneyFormat;

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.hidden = !message;
      errorEl.textContent = message || '';
    };

    const refreshTotal = () => {
      const items = selectedBoxItems(box);
      const cents = items.reduce((sum, item) => sum + (item.cents || 0), 0);
      const compareCents = items.reduce((sum, item) => sum + (item.compareCents || 0), 0);
      const formatted = formatBoxMoney(cents, moneyFormat);
      if (totalEl) totalEl.textContent = formatted;
      if (compareEl) {
        const showCompare = compareCents > cents;
        compareEl.hidden = !showCompare;
        if (showCompare) compareEl.textContent = formatBoxMoney(compareCents, moneyFormat);
      }
      const planGroup = box.closest('[data-mdp-plan-group]');
      const planRadio = planGroup && planGroup.querySelector('[data-mdp-box-plan]');
      if (planRadio) {
        planRadio.dataset.mdpPlanPrice = formatted;
        if (planRadio.checked) {
          const target = planGroup.querySelector('[data-mdp-plan-price-target]');
          if (target) target.textContent = formatted;
        }
      }
    };

    box.querySelectorAll('[data-mdp-box-slot]').forEach((slot) => {
      slot.addEventListener('change', refreshTotal);
    });
    refreshTotal();

    if (!addBtn) return;
    addBtn.addEventListener('click', async () => {
      const items = selectedBoxItems(box);
      if (items.some((item) => !item.id || item.disabled)) {
        showError(box.dataset.unavailable);
        return;
      }

      showError('');
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addBtn.classList.add('is-loading');
      if (addLabel) addLabel.textContent = box.dataset.adding;

      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems && cartItems.dataset.sectionId;
      const merged = [];
      items.forEach((item) => {
        const existing = merged.find((row) => String(row.id) === String(item.id));
        if (existing) existing.quantity += 1;
        else merged.push({ id: Number(item.id) || item.id, quantity: 1, properties: { _mdp_box: 'pick-three' } });
      });
      const payload = { items: merged };
      if (sectionId) payload.sections = sectionId;

      try {
        const routes = (window.Theme && Theme.routes) || {};
        const addUrl = routes.cart_add_url || '/cart/add.js';
        const cartUrl = routes.cart_url || '/cart.js';
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          showError((json && json.description) || box.dataset.error);
          return;
        }

        let itemCount = items.length;
        let cartResource = json;
        try {
          const cartRes = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
          if (cartRes.ok) {
            cartResource = await cartRes.json();
            itemCount = Number(cartResource.item_count) || itemCount;
          }
        } catch (cartErr) {
          console.warn('[mdp] household box cart refresh failed', cartErr);
        }

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartResource,
            data: {
              source: 'mdp-household-box',
              itemCount,
              sections: json.sections,
            },
          },
        }));
      } catch (err) {
        console.error('[mdp] household box add error', err);
        showError(box.dataset.error);
      } finally {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-busy');
        addBtn.classList.remove('is-loading');
        if (addLabel) addLabel.textContent = box.dataset.add;
      }
    });
  });
};

const initMdpDesign = (root = document) => {
  initTabs(root);
  initGallery(root);
  initZoomDialogs(root);
  initQuantity(root);
  initPlanPrices(root);
  initSticky(root);
  initSubscriptionAdd(root);
  initHouseholdBox(root);
  initLazyVideos(root);
  initArticleToc(root);
};

initMdpDesign();
document.addEventListener('shopify:section:load', (event) => initMdpDesign(event.target));
